import { BookOpen, type LucideIcon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface SubjectCardProps {
  title: string;
  description?: string;
  iconComponent: LucideIcon;
  color: "history" | "science" | "math" | "social" | "economics";
  progress?: number;
  questionsCount?: number;
  studyTime?: string;
  difficulty?: "Easy" | "Medium" | "Hard";
  href: string;
  variant?: "compact" | "detailed";
  actions?: React.ReactNode; // optional footer actions (e.g., Add/Remove)
}

const colorClasses = {
  history: "bg-subjects-history/10 text-subjects-history border-subjects-history/20",
  science: "bg-subjects-science/10 text-subjects-science border-subjects-science/20",
  math: "bg-subjects-math/10 text-subjects-math border-subjects-math/20",
  social: "bg-subjects-social/10 text-subjects-social border-subjects-social/20",
  economics: "bg-subjects-economics/10 text-subjects-economics border-subjects-economics/20",
};

const iconBgClasses = {
  history: "bg-subjects-history",
  science: "bg-subjects-science",
  math: "bg-subjects-math",
  social: "bg-subjects-social",
  economics: "bg-subjects-economics",
};

export function SubjectCard({
  title,
  description,
  iconComponent: IconComponent,
  color,
  progress,
  questionsCount,
  studyTime,
  difficulty,
  href,
  variant = "detailed",
  actions,
}: SubjectCardProps) {
  return (
    <Card className="group transition-all duration-300 hover:shadow-medium hover:-translate-y-1 border bg-gradient-card">
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className={cn(
            "p-3 rounded-lg flex items-center justify-center text-white",
            iconBgClasses[color]
          )}>
            <IconComponent className="h-6 w-6" />
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <h3 className="font-semibold text-lg text-foreground group-hover:text-primary transition-colors">
              {title}
            </h3>
            {description && (
              <p className="text-sm text-muted-foreground mt-1">
                {description}
              </p>
            )}
          </div>

          {/* Compact meta row: only show questions count (no progress bar / hours per week) */}
          {(questionsCount !== undefined) && (
            <div className="text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <BookOpen className="h-4 w-4" />
                <span>{questionsCount} questions</span>
              </div>
            </div>
          )}

          {actions && (
            <div className="mt-3 flex gap-2">
              {actions}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}